import { useEffect, useState, useCallback } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Image as ImageIcon, Film } from "lucide-react";

const CATS = ["solar", "mining", "media"];

export default function MediaManager() {
  const [media, setMedia] = useState([]);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", category: "solar", location: "", media_type: "photo" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => api.get("/media").then(({ data }) => setMedia(data)).catch((e) => console.error(e)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Select a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Media uploaded");
      setFile(null); setMeta({ title: "", category: "solar", location: "", media_type: "photo" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/media/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B3B39]">Media Gallery Manager</h1><p className="text-slate-600 mt-1">Upload and manage 4K photos &amp; videos.</p></div>

      <form onSubmit={upload} className="oj-card p-6 mb-8" data-testid="media-upload-form">
        <h3 className="oj-h3 mb-5">Upload New Asset</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input placeholder="Title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="oj-input" data-testid="media-title" />
          <input placeholder="Location" value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} className="oj-input" data-testid="media-location" />
          <select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className="oj-input" data-testid="media-category">
            {CATS.map((c) => <option key={c} value={c} className="bg-teal-950 capitalize">{c}</option>)}
          </select>
          <select value={meta.media_type} onChange={(e) => setMeta({ ...meta, media_type: e.target.value })} className="oj-input" data-testid="media-type">
            <option value="photo" className="bg-teal-950">Photo</option>
            <option value="video" className="bg-teal-950">Video</option>
          </select>
          <label className="flex items-center gap-3 oj-input cursor-pointer sm:col-span-2">
            <Upload className="w-5 h-5 text-teal-600 shrink-0" />
            <span className="text-sm text-slate-600 truncate">{file ? file.name : "Choose image or video file"}</span>
            <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="media-file-input" />
          </label>
        </div>
        <button disabled={uploading} className="oj-btn-gold mt-5" data-testid="media-upload-submit">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Asset</button>
      </form>

      {media.length === 0 ? (
        <div className="oj-card p-12 text-center text-slate-500"><ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />No uploaded media yet. Uploads appear here and on the public gallery.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {media.map((m) => {
            const isVideo = m.media_type === "video" || m.content_type?.startsWith("video");
            return (
              <div key={m.id} className="oj-card overflow-hidden group" data-testid={`media-card-${m.id}`}>
                <div className="relative h-40 bg-teal-950">
                  {isVideo ? <video src={fileUrl(m.storage_path)} className="w-full h-full object-cover" muted /> : <img src={fileUrl(m.storage_path)} alt={m.title} className="w-full h-full object-cover" />}
                  <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-lg bg-red-600/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                  {isVideo && <Film className="absolute bottom-2 left-2 w-4 h-4 text-white" />}
                </div>
                <div className="p-3"><div className="text-sm font-medium text-white truncate">{m.title}</div><div className="text-xs text-slate-500 capitalize">{m.category} • {m.location || "—"}</div></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
